import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { categoryLabel, deletePost } from '../api/community';
import PostGallery from './PostGallery';

// Renders body with line breaks preserved (CSS white-space: pre-wrap).
// Phase 2 has no markdown rendering — plain text only.

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function PostDetail({ post, onChange }) {
  if (!post) return null;
  const isEdited = post.updatedAt && post.updatedAt !== post.createdAt;
  return (
    <article style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      padding: '28px 28px',
    }}>
      <div style={{
        display: 'inline-block',
        padding: '3px 10px', borderRadius: 12,
        fontSize: '0.75rem', fontWeight: 600,
        background: 'rgba(74, 159, 255, 0.15)', color: 'var(--primary)',
        marginBottom: 12,
      }}>
        {categoryLabel(post.category)}
      </div>

      <h1 style={{
        fontSize: '1.5rem', fontWeight: 700,
        margin: '0 0 12px 0', lineHeight: 1.3,
      }}>
        {post.title}
      </h1>

      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: '0.85rem', color: 'var(--text-muted)',
        paddingBottom: 16, borderBottom: '1px solid var(--border)',
        marginBottom: 24, flexWrap: 'wrap', gap: 8,
      }}>
        <span>{post.nickname || '익명'}</span>
        <span>
          {formatDate(post.createdAt)}
          {isEdited && (
            <span style={{ color: 'var(--text-dim)', marginLeft: 6 }}>
              (수정됨 {formatDate(post.updatedAt)})
            </span>
          )}
        </span>
      </div>

      <PostActions post={post} onChange={onChange} />

      <PostGallery images={post.imageUrls} alt={post.title} />

      <div style={{
        fontSize: '1rem', lineHeight: 1.7,
        color: 'var(--text)', whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}>
        {post.body}
      </div>
    </article>
  );
}

function PostActions({ post, onChange }) {
  const { user, isAdmin, getIdToken } = useAuth();
  const navigate = useNavigate();
  const userSub = user?.userId || user?.username;
  const isOwner = userSub === post.authorSub;
  const canEdit = isOwner || isAdmin;
  const canDelete = isOwner || isAdmin;

  if (!canEdit && !canDelete) return null;

  const handleDelete = async () => {
    if (!window.confirm('이 글을 삭제할까요? 댓글도 함께 삭제됩니다.')) return;
    try {
      const token = await getIdToken();
      if (!token) throw new Error('세션이 만료되었습니다');
      await deletePost({ token, postId: post.postId });
      navigate(`/community/${post.category}`, { replace: true });
    } catch (err) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  return (
    <div style={{
      display: 'flex', gap: 8, justifyContent: 'flex-end',
      marginTop: -16, marginBottom: 24,
    }}>
      {canEdit && (
        <Link
          to={`/community/edit/${encodeURIComponent(post.postId)}`}
          className="btn btn-outline"
          style={{ padding: '6px 14px', fontSize: '0.82rem' }}
        >
          수정
        </Link>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={handleDelete}
          className="btn btn-outline"
          style={{ padding: '6px 14px', fontSize: '0.82rem', color: '#ff5252', borderColor: '#ff5252' }}
        >
          삭제
        </button>
      )}
    </div>
  );
}
